import { getCurrentUser, signIn } from 'aws-amplify/auth';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import type { CurrentUser, LoginCredentials } from '@/types/auth';

export type AuthResult = {
  success: boolean;
  userInfo?: CurrentUser;
  error?: string;
};

export const useAuth = () => {
  const { t } = useTranslation();

  const authenticate = useCallback(
    async (credentials: LoginCredentials): Promise<AuthResult> => {
      try {
        const res = await signIn(credentials);

        if (!res.isSignedIn) {
          return { success: false, error: '認証に失敗しました' };
        }

        const userInfo = await getCurrentUser();

        return {
          success: true,
          userInfo: {
            userId: userInfo.userId,
            username: userInfo.username,
          },
        };
      } catch (error: unknown) {
        // TODO: i18n対応
        let errorMessage = t('login.unexpectedError');

        if (error instanceof Error) {
          // AWS Amplifyのエラーメッセージを日本語に翻訳
          const message = error.message.toLowerCase();
          if (message.includes('user does not exist') || message.includes('user not found')) {
            errorMessage = 'ユーザーが存在しません。';
          } else if (
            message.includes('incorrect username or password') ||
            message.includes('invalid username or password') ||
            message.includes('wrong password') ||
            message.includes('authentication failed')
          ) {
            errorMessage = 'ユーザー名またはパスワードが違います。';
          } else {
            errorMessage = `認証エラー: ${error.message}`;
          }
        }

        return { success: false, error: errorMessage };
      }
    },
    [t],
  );

  const getCurrentUserInfo = useCallback(async (): Promise<CurrentUser | null> => {
    try {
      const userInfo = await getCurrentUser();

      return {
        userId: userInfo.userId,
        username: userInfo.username,
      };
    } catch (_error) {
      return null;
    }
  }, []);

  return {
    authenticate,
    getCurrentUserInfo,
  };
};
